import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import type { HttpInterceptor } from '@angular/common/http';
import { MyHttpHandler } from './my-http-extensions';
import { HttpBaseUrlAppenderInterceptor } from './interceptors/http-baseUrlAppender.interceptor';
import { HttpForceCamelCaseInterceptor } from './interceptors/http-force-camelCase.interceptor';
import { HttpForcePascalCaseInterceptor } from './interceptors/http-force-PascalCase.interceptor';
import { HttpLocalCalendarDateBodyInterceptor } from './interceptors/http-local-calendar-date-body.interceptor';
import { HttpReviveDatesInterceptor } from './interceptors/http-revive-dates.interceptor';
import { HttpUPSInterceptor } from './interceptors/http-ups.interceptor';
import { ResponseCasingEnum } from './response-enum';
import {
  IMyHttpClientFactory,
  MyHttpClientFactoryOptions,
} from './my-http-client-factory.interface';
import { HTTP_AUTH_INTERCEPTOR } from './interceptors/http-auth-interceptor.token';

/** Interceptors applied to clients created outside Angular's global HTTP chain. */
export const MY_HTTP_CLIENT_INTERCEPTORS = new InjectionToken<
  readonly HttpInterceptor[]
>('MY_HTTP_CLIENT_INTERCEPTORS');

@Injectable({
  providedIn: 'root',
})
export class MyHttpClientFactory implements IMyHttpClientFactory {
  constructor(
    private backend: HttpBackend,
    @Inject(HTTP_AUTH_INTERCEPTOR)
    private authenticationInterceptor: HttpInterceptor,
    private httpUPSInterceptor: HttpUPSInterceptor,
    private forceCamelCaseInterceptor: HttpForceCamelCaseInterceptor,
    private forcePascalCaseInterceptor: HttpForcePascalCaseInterceptor,
    private reviveDatesInterceptor: HttpReviveDatesInterceptor,
    private localCalendarDateBodyInterceptor: HttpLocalCalendarDateBodyInterceptor,
    @Optional()
    @Inject(MY_HTTP_CLIENT_INTERCEPTORS)
    private readonly extensionInterceptors:
      | readonly HttpInterceptor[]
      | null = null
  ) {}

  public createHttpClient(
    baseUrl: string,
    auth = true,
    casing: ResponseCasingEnum = ResponseCasingEnum.Default,
    options: MyHttpClientFactoryOptions = {}
  ) {
    const { reviveDates = true, localCalendarDateBody = true } = options;

    const interceptors: HttpInterceptor[] = [];

    // The base URL is resolved per-request inside the interceptor (see below) so a
    // dev/PR-only API URL override takes effect WITHOUT a page reload.
    if (baseUrl) interceptors.push(new HttpBaseUrlAppenderInterceptor(baseUrl));

    if (auth) {
      interceptors.push(this.authenticationInterceptor);
      interceptors.push(this.httpUPSInterceptor);
    }

    // NOTE: we sue == instead of === in case of non-typed value is passed due to binding on UI...
    if (casing === ResponseCasingEnum.CamelCase)
      interceptors.push(this.forceCamelCaseInterceptor);
    else if (casing === ResponseCasingEnum.PascalCase)
      interceptors.push(this.forcePascalCaseInterceptor);

    if (localCalendarDateBody) {
      interceptors.push(this.localCalendarDateBodyInterceptor);
    }

    if (reviveDates) {
      interceptors.push(this.reviveDatesInterceptor);
    }

    if (this.extensionInterceptors?.length) {
      interceptors.push(...this.extensionInterceptors);
    }

    const handler = new MyHttpHandler(this.backend, interceptors);
    const client = new HttpClient(handler);
    return client;
  }
}
